import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./AllOpenOrdersPage.css";

const DEFAULT_PAGE_LIMIT = 50;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "Open Orders";
const OPEN_STATUS_FILTER = "PENDING,PARTIALLY EXECUTED";
const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;
const HTTP_ERROR_STATUSES = new Set([400, 404, 500]);
const GENERIC_CANCEL_ERROR = "Failed to cancel order, please try again";

const unwrapId = (raw) => {
  if (raw == null || raw === "") return "";
  if (typeof raw === "object") {
    return unwrapId(raw.$oid || raw._id || raw.id);
  }
  return String(raw).trim();
};

/** Mongo document id from the order list — not the display orderId (e.g. Y541ET4). */
const getOrderDocumentId = (row) => {
  if (!row) return "";
  const displayId = unwrapId(row.orderId);
  const candidates = [row._id, row.id];
  for (const raw of candidates) {
    const id = unwrapId(raw);
    if (OBJECT_ID_RE.test(id)) return id;
  }
  for (const raw of candidates) {
    const id = unwrapId(raw);
    if (id && id !== displayId) return id;
  }
  return unwrapId(row._id) || unwrapId(row.id) || "";
};

const canCancelOpenOrder = (row) => {
  const status = String(row?.status || "")
    .toUpperCase()
    .replace(/_/g, " ")
    .trim();
  if (!status) return true;
  return status === "PENDING" || status === "PARTIALLY EXECUTED" || status === "PARTIAL";
};

const formatCancelSuccessToast = (res) => {
  const data = res?.data && !Array.isArray(res.data) ? res.data : {};
  const parts = [res?.message || "Order cancelled"];
  if (data.refundedQuantity != null && String(data.refundedQuantity) !== "") {
    const unit = data.refundedCurrency ? ` ${data.refundedCurrency}` : "";
    parts.push(`Refunded ${data.refundedQuantity}${unit}`);
  }
  if (data.status) parts.push(data.status);
  return parts.join(" · ");
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const rowToExport = (row) => ({
  "Order ID": row.orderId || row._id || "",
  Date: row.createdAt ?? "",
  "User ID": row.user?.uuid || row.user_id || "",
  "User Email": row.user?.email || row.user_email || "",
  Mobile: row.user?.phone || row.user_mobileNumber || "",
  Pair: `${row.pay_currency || row.quote_currency_id}/${row.ask_currency || row.base_currency_id}`,
  Side: row.side ?? "",
  "Order Type": row.order_type ?? "",
  Price: row.price ?? "",
  Average: row.avg_execution_price ?? "",
  Quantity: row.quantity ?? "",
  Filled: row.filled ?? "",
  Remaining: row.remaining ?? "",
  Fee: row.total_fee ?? "",
  "Fee Currency": row.side === "BUY" ? row.ask_currency : row.pay_currency,
});

const AllOpenOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 1000);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageLimit]);

  const buildParams = useCallback(
    (pageNum = page, limitNum = pageLimit) => {
      const params = {
        page: pageNum,
        limit: limitNum,
        status: OPEN_STATUS_FILTER,
      };
      if (debouncedSearch) params.user = debouncedSearch;
      return params;
    },
    [page, pageLimit, debouncedSearch]
  );

  const fetchOpenOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await AdminService.getGlobalSpotOrders(buildParams());
      if (res?.success && Array.isArray(res.data)) {
        setOrders(res.data);
        setTotalPages(res.pagination?.total_pages || res.pagination?.totalPages || 1);
        setTotalCount(res.pagination?.total ?? res.data.length);
      } else {
        setOrders([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Failed to fetch open orders", err);
      setError("Failed to load open orders.");
      setOrders([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchOpenOrders();
  }, [fetchOpenOrders]);

  const fetchAllFilteredOrders = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getGlobalSpotOrders(buildParams(currentPage, EXPORT_BATCH));
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.totalPages || res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllFilteredOrders();
      if (!allRows.length) {
        setToast({ type: "error", message: "No open orders match your filters to export." });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const exportData = allRows.map(rowToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `open_orders_${dateStamp}.xlsx`);

      setToast({ type: "success", message: `Exported ${allRows.length} open order(s) to Excel.` });
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      console.error(e);
      setToast({ type: "error", message: e?.message || "Failed to export Excel file." });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageLimitChange = (e) => {
    setPageLimit(Number(e.target.value) || DEFAULT_PAGE_LIMIT);
    setPage(1);
  };

  const closeCancelModal = () => {
    if (cancelLoading) return;
    setCancelOrder(null);
    setCancelError("");
  };

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const handleConfirmCancel = async () => {
    const order_id = getOrderDocumentId(cancelOrder);
    if (!order_id) {
      setCancelError("This order is missing a document id.");
      return;
    }
    setCancelLoading(true);
    setCancelError("");
    try {
      const res = await AdminService.cancelAdminSpotOrder({ order_id });
      if (HTTP_ERROR_STATUSES.has(Number(res?.httpStatus))) {
        console.error("Admin cancel order failed", res);
        setCancelError(GENERIC_CANCEL_ERROR);
        return;
      }
      if (res?.success) {
        const cancelledId = order_id;
        setOrders((prev) =>
          prev.filter((row) => getOrderDocumentId(row) !== cancelledId)
        );
        setTotalCount((n) => Math.max(0, n - 1));
        setCancelOrder(null);
        showToast("success", formatCancelSuccessToast(res));
        return;
      }
      setCancelError(res?.message || GENERIC_CANCEL_ERROR);
    } catch (err) {
      console.error("Admin cancel order failed", err);
      setCancelError(GENERIC_CANCEL_ERROR);
    } finally {
      setCancelLoading(false);
    }
  };

  const pageStart = orders.length === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = orders.length === 0 ? 0 : (page - 1) * pageLimit + orders.length;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>All Open Orders</span>
          </p>
          <h1 className="dashboard-hero__title">All Open Orders</h1>
          <p className="dashboard-hero__subtitle">
            View and manage all open orders
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-list" />
        </div>
      </section>

      {toast && (
        <div className={`openorders-toast openorders-toast--${toast.type}`} role="status">
          <i className={`fas fa-${toast.type === "success" ? "check-circle" : "exclamation-circle"}`} />
          {toast.message}
        </div>
      )}

      <div className="dashboard-body">
        <section className="openorders-section">
          <div className="openorders-section__header">
            <div className="openorders-section__title-wrap">
              <span className="openorders-section__icon">
                <i className="fas fa-clipboard-list" />
              </span>
              <h2 className="openorders-section__title">Open Orders</h2>
            </div>
            <div className="openorders-section__stats openorders-section__stats--actions">
              <span className="openorders-stat">
                <strong>{orders.length}</strong> on page
                {totalCount > 0 && (
                  <>
                    {" "}
                    (<strong>{totalCount}</strong> total)
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="openorders-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="openorders-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered open orders as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
              <button
                type="button"
                className="openorders-refresh-btn"
                onClick={fetchOpenOrders}
                disabled={loading}
                title="Refresh"
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>
          {error && (
            <p className="openorders-empty" style={{ color: "#dc2626", paddingBottom: 0 }}>
              {error}
            </p>
          )}
          <div className="openorders-search-wrap">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search by email, UUID or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="openorders-search-input"
            />
            <label className="openorders-page-limit">
              Rows per page
              <select
                value={pageLimit}
                onChange={handlePageLimitChange}
                className="openorders-search-input openorders-page-limit__select"
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="openorders-table-wrap">
            <table className="openorders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>User ID</th>
                  <th>User Email</th>
                  <th>Mobile</th>
                  <th>Pair</th>
                  <th>Side</th>
                  <th>Order Type</th>
                  <th>Price</th>
                  <th>Average</th>
                  <th>Quantity</th>
                  <th>Filled</th>
                  <th>Remaining</th>
                  <th> Fee</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && orders.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="openorders-empty">
                      <i className="fas fa-spinner fa-spin" /> Loading open orders…
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={15} className="openorders-empty">
                      No open orders match your search.
                    </td>
                  </tr>
                ) : (
                  orders.map((row) => (
                    <tr key={getOrderDocumentId(row) || row.orderId || row._id}>
                      <td><span className="openorders-order-id">{row.orderId || row._id || row.id}</span></td>
                      <td>{formatDate(row.createdAt)}</td>
                      <td>
                        {(row.user?.id || row.user_id) ? (
                          <Link
                            to={`/dashboard/trader/${row.user?.id || row.user_id}`}
                            state={{ trader: row.user }}
                            style={{ color: "#dc2626", fontWeight: 600, textDecoration: "none" }}
                            title="View trader details"
                          >
                            {row.user?.uuid || row.user_id}
                          </Link>
                        ) : (
                          row.user?.uuid || row.user_id || "---"
                        )}
                      </td>
                      <td>{row.user?.email || row.user_email}</td>
                      <td>{row.user?.phone || row.user_mobileNumber}</td>
                      <td><strong>{row.pay_currency || row.quote_currency_id}/{row.ask_currency || row.base_currency_id}</strong></td>
                      <td>
                        <span className={`openorders-side openorders-side--${row.side?.toLowerCase()}`}>
                          {row.side}
                        </span>
                      </td>
                      <td>{row.order_type}</td>
                      <td>{row.price}</td>
                      <td>{row.avg_execution_price || "N/A"}</td>
                      <td>{typeof row.quantity === "number" ? row.quantity.toLocaleString("en-IN", { maximumFractionDigits: 4 }) : row.quantity}</td>
                      <td>{typeof row.filled === "number" ? row.filled.toLocaleString("en-IN", { maximumFractionDigits: 4 }) : row.filled}</td>
                      <td>{typeof row.remaining === "number" ? row.remaining.toLocaleString("en-IN", { maximumFractionDigits: 4 }) : row.remaining}</td>
                      <td>{row.total_fee || "0"} {row.side === "BUY" ? row.ask_currency : row.pay_currency}</td>
                      <td>
                        {canCancelOpenOrder(row) ? (
                          <button
                            type="button"
                            className="openorders-cancel-btn"
                            disabled={cancelLoading}
                            onClick={() => {
                              setCancelError("");
                              setCancelOrder(row);
                            }}
                          >
                            Cancel
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalCount > 0 && (
            <div className="openorders-pagination">
              <span className="openorders-pagination__info">
                Showing {pageStart}–{pageEnd} of {totalCount}
                {totalPages > 1 && <> · Page {page} of {totalPages}</>}
              </span>
              {totalPages > 1 && (
                <div className="openorders-pagination__buttons">
                  <button
                    type="button"
                    className="openorders-pagination__btn"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <i className="fas fa-chevron-left" /> Prev
                  </button>
                  <button
                    type="button"
                    className="openorders-pagination__btn"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next <i className="fas fa-chevron-right" />
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {cancelOrder && (
        <div
          className="openorders-cancel-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="openorders-cancel-title"
          onClick={closeCancelModal}
        >
          <div className="openorders-cancel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="openorders-cancel-modal__head">
              <h2 id="openorders-cancel-title">Cancel this order?</h2>
              <p>Locked funds will be refunded to the user.</p>
            </div>
            <div className="openorders-cancel-modal__body">
              <div className="openorders-cancel-modal__summary">
                <div>
                  <strong>Order:</strong> {cancelOrder.orderId || cancelOrder._id || cancelOrder.id || "—"}
                </div>
                <div>
                  <strong>User:</strong>{" "}
                  {cancelOrder.user?.email ||
                    cancelOrder.user_email ||
                    cancelOrder.user?.uuid ||
                    cancelOrder.user_id ||
                    "—"}
                </div>
                <div>
                  <strong>Side:</strong> {cancelOrder.side || "—"}
                  {cancelOrder.order_type ? ` · ${cancelOrder.order_type}` : ""}
                </div>
                <div>
                  <strong>Qty / filled / remaining:</strong> {cancelOrder.quantity ?? "—"} /{" "}
                  {cancelOrder.filled ?? "—"} / {cancelOrder.remaining ?? "—"}
                </div>
              </div>
              {cancelError ? <p className="openorders-cancel-modal__error">{cancelError}</p> : null}
              <p className="openorders-cancel-modal__warn">
                <i className="fas fa-exclamation-triangle" aria-hidden />
                This cannot be undone. Confirm only if you intend to cancel this open order.
              </p>
            </div>
            <div className="openorders-cancel-modal__foot">
              <button
                type="button"
                className="openorders-cancel-modal__keep"
                onClick={closeCancelModal}
                disabled={cancelLoading}
              >
                Keep order
              </button>
              <button
                type="button"
                className="openorders-cancel-modal__confirm"
                onClick={handleConfirmCancel}
                disabled={cancelLoading}
              >
                <i className={`fas ${cancelLoading ? "fa-spinner fa-spin" : "fa-ban"}`} />
                {cancelLoading ? "Cancelling…" : "Yes, cancel order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AllOpenOrdersPage;
